/* eslint-disable max-lines */
"use client";
import PropTypes from "prop-types";
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
// import ReactDOM from "react-dom";
import dynamic from "next/dynamic";
import {
  requestToGetCustomerDetails,
  requestToOpenLoginDrawer,
  requestToResetLoginSuccessfullToast,
} from "../../../store/slices/loginSignupSlice";
import { requestToGetWebFeatureControls } from "../../../store/slices/webFeatureControls";
import { DesktopMegaMenu } from "../megaMenu";
import FNPIcon from "./fnpIcon/index";
import HeaderToolBar from "./headerToolBar";
import { requestToGetCurrency, requestToGetCurrencyFlagIcon } from "../../../store/slices/common/currencyLists";
import DesktopSearch from "../desktopSearch";
import { initiateFus } from "../../../store/slices/common/fus";
import { requestToFetchFnpCashBalance, requestToFetchFnpLabel } from "../../../store/slices/fnpCash";
import isEmpty from "lodash.isempty";
import { COUNTRIES, LOTTIE_ICONS, THRESHOLD_HEADER_FOR_SEARCH } from "../../../constants/common/common";
import { setGA4LoginAndSignUp } from "../../../analytics/gaEvents";
import useAnalytics from "../../../analytics/useAnalytics";
import { GA4_EVENTS, SOCIAL_LOGIN_KEY } from "../../../analytics/gaConstant";
import { cleverTapCommonEvents } from "../../../analytics/clevertapConstants";
import { getSessionStorage, removeSessionStorage } from "../../../utils/storage/sessionStorageUtil";
import { getLottieIcons, getMegaMenuContentAPI, onClevertapPublish } from "../../../api/common";
import { fetchDefaultSearchPageFeed } from "../../../utils/dynamicHomeUtils/defaultSearchFeed";
import { useABTestingView } from "../../../customHooks/clevertap/useABTestingView";
import { execDataLayer } from "../../../utils/cache-layer/cacheLayer";
import { requestToSetIcons } from "../../../store/slices/lottieIconsSlice";
import {
  getLocalStorageItem,
  removeLocalStorageItem,
  setLocalStorageItem,
} from "../../../utils/storage/localStorageUtil";
import { formatClassNames } from "../../../utils/common";
import ButtonComponent from "../../atoms/button";
import Toast from "../../atoms/toast";
import { MY_GROUPS, NEW_GROUP } from "../../../constants/pageConstants/secretSanta";
import LocationLockHeader from "./locationLockHeader";
import ClearCart from "../clearCart";
import { requestToFetchSpecialCountries } from "../../../store/slices/deliveryAddress";
import { responseGetCartItems } from "../../../store/slices/cartSlice";
import { openSiteChat } from "../../../customHooks/useFnpChat/openSiteChat";
import { consumeHeaderAccountLoginRedirect } from "../../../utils/login/headerAccountLoginRedirect";

const LoginSignupDrawer = dynamic(() => import("../../../containers/LoginSignupPage/LoginSignupDrawer"), {
  ssr: false,
});

/** Homepage mobile overlay: white bg starts at 50% once scroll begins, then ramps to 100%. */
const HEADER_BG_OPACITY_MIN = 0.7;
const HEADER_BG_OPACITY_MAX = 1;
/** Scroll distance (px) after the threshold over which opacity goes from 50% → 100%. */
const HEADER_BG_FADE_DISTANCE_PX = 30;

/**
 * Returns the current vertical scroll offset used by the header.
 * @returns {number} Absolute scroll offset in pixels.
 */
const getHeaderScrollOffset = () => Math.abs(document.documentElement.getBoundingClientRect().top);

/**
 * Returns the height of the optional app install banner above the page.
 * @returns {number} Banner height in pixels, or 0 when absent.
 */
const getAppBannerHeight = () =>
  document.getElementById("homeAppBanner")?.clientHeight || document.getElementById("appBanner")?.clientHeight || 0;

/**
 * Maps scroll position to white-background opacity for mobile homepage overlay header.
 * At/above the top threshold: fully transparent (0).
 * Past threshold: 0.5 → 1.0 over {@link HEADER_BG_FADE_DISTANCE_PX}.
 *
 * @param {number} scrollY - Current scroll offset.
 * @param {number} bannerHeight - App banner height included in the threshold.
 * @returns {number} Opacity between 0 and 1.
 */
const getTransparentHeaderBgOpacity = (scrollY, bannerHeight) => {
  const threshold = THRESHOLD_HEADER_FOR_SEARCH + bannerHeight;
  if (scrollY < threshold) {
    return 0;
  }
  const progress = Math.min(1, (scrollY - threshold) / HEADER_BG_FADE_DISTANCE_PX);
  return HEADER_BG_OPACITY_MIN + progress * (HEADER_BG_OPACITY_MAX - HEADER_BG_OPACITY_MIN);
};

/**
 * Builds class names for the main header shell (`#header-child`).
 * Keeps mobile/desktop layout, transparent overlay, scroll shadow, and India top-banner offset in one place.
 *
 * @param {Object} params - Header shell state/flags.
 * @param {boolean} params.isMobile - Mobile layout uses relative positioning; desktop uses fixed.
 * @param {boolean} params.isTransparentOnMobile - When true on mobile, header can overlay content at page top.
 * @param {boolean} params.isScrolled - Whether the page has scrolled past the mobile threshold.
 * @param {boolean} params.isAddShadow - Whether to show the header drop shadow.
 * @param {boolean} params.isTopBanner - Whether the free-shipping (or similar) top banner is shown.
 * @param {string} params.catalogId - Catalog/country id used for India-only top offset.
 * @returns {string} Combined className string for the header shell.
 */
const getHeaderShellClassNames = ({
  isMobile,
  isTransparentOnMobile,
  isScrolled,
  isAddShadow,
  isTopBanner,
  catalogId,
}) => {
  const isHomepageMobileOverlay = isMobile && isTransparentOnMobile;

  // Mobile scrolls with the page; desktop sticks to the viewport
  const layoutClasses = isMobile
    ? `relative ${!isTransparentOnMobile ? "border-b-1 border-lightGrey" : ""} px-16 py-10`
    : "fixed";

  // Overlay mode: no Tailwind bg classes — opacity is driven by inline rgba style
  const baseClasses = isHomepageMobileOverlay
    ? "z-[100] mb-8 grid w-full grid-cols-1 transition-[background-color] duration-150 md:p-0"
    : "bg-white z-[100] mb-8 grid w-full grid-cols-1 md:p-0";

  // Non-overlay: transparent only at top of transparent mobile pages; otherwise solid white
  const backgroundClasses = isHomepageMobileOverlay
    ? ""
    : isMobile && isTransparentOnMobile && !isScrolled
      ? "bg-transparent"
      : "bg-white-900";

  // Shadow after scroll (mobile threshold / any desktop scroll)
  const shadowClasses = isAddShadow && "shadow-[0_2px_4px_rgba(0,0,0,0.1)]";

  // Desktop India: sit below the promo top banner; otherwise flush to top
  const sitsBelowIndiaTopBanner = !isMobile && isTopBanner && catalogId === COUNTRIES?.INDIA;
  const topOffsetClasses = sitsBelowIndiaTopBanner
    ? "top-[46px] xml:top-[50.50px] xmll:top-[53px] xll:top-[58px] xxl:top-[66px]"
    : "top-0";

  return formatClassNames(layoutClasses, baseClasses, backgroundClasses, shadowClasses, topOffsetClasses);
};

/**
 * Header component represents the header section of the page.
 * It includes the FNPIcon, LocationLock, SearchBar, and HeaderButtons components.
 *
 * @param {Object} props - The props for the Header component.
 * @param {boolean} props.isFromPDP - Indicates if the header is being rendered on the product details page.
 * @param {boolean} props.isMobile - Indicates if the current device is mobile.
 * @param {string} props.catalogId - The catalog identifier (e.g., "india").
 * @param {Array} props.preloadLinks - Array of link objects for preloading resources.
 * @returns {JSX.Element} The JSX representation of the Header component.
 */
const Header = ({
  isFromPDP = false,
  isMobile,
  catalogId = "india",
  // preloadLinks,
  isLoginPage = false,
  serverSelectedCurrency,
  currencyListWithHeaders,
  searchQuery,
  isShowSearchIcon = true,
  menuData,
  isTopBanner = false,
  isSecretSanta = false,
  isTransparentOnMobile = false,
  headerTextIconColor,
  // On by default: the Finder pill belongs beside the search bar everywhere the header
  // renders. Pass false to suppress it on a specific surface.
  isGiftFinderEnabled = true,
  isSearchEnabled = true,
  isShowLocationLock = true,
}) => {
  const dispatch = useDispatch();
  const headerRef = useRef(null);
  const { trackEvent, onUserLogin } = useAnalytics();
  const [isSearchIconVisible, setIsSearchIconVisible] = useState(false);
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [isAddShadow, setIsAddShadow] = useState(false);
  const [defaultSearchWidgets, setDefaultSearchWidgets] = useState([]);
  const [isDefaultLoading, setIsDefaultLoading] = useState(false);
  const [hash, setHash] = useState("");
  const [isScrolled, setIsScrolled] = useState(false);
  /** White bg opacity for homepage mobile overlay (0 at top, then 0.5 → 1 while scrolling). */
  const [headerBgOpacity, setHeaderBgOpacity] = useState(0);
  const [clientMenuData, setClientMenuData] = useState([]);
  const { autoSuggestion, recallABTestingHook } = useABTestingView();
  const urlParams = new URLSearchParams(typeof window !== "undefined" ? window?.location?.search : "");
  const isHomepageMobileOverlay = isMobile && isTransparentOnMobile;

  const { loginSignUp, loginDetail, checkLoginStatus, availableBalance, isShowCartEmpty, selectedCurrency, cartCount } =
    useSelector(
      (state) => ({
        loginSignUp: state?.loginSignUp,
        loginDetail: state?.fus?.loginDetail || {},
        checkLoginStatus: state.fus.loginDetail?.loggedin,
        availableBalance: state.fnpCash?.availableBalance,
        isShowCartEmpty: state.locationLock?.isShowCartEmpty,
        selectedCurrency: state?.currencyList?.userSelectedCurrency,
        cartCount: state?.cart?.cartCount,
      }),
      shallowEqual,
    );

  /**
   * Resets login success toast state after dismiss.
   */
  const onLoginSuccessToastChange = () => {
    dispatch(requestToResetLoginSuccessfullToast());
  };

  const loginSuccessToast =
    !isMobile && loginSignUp?.loginSignUpSuccessfully ? (
      <Toast
        variant="success"
        isToast={true}
        header={loginSignUp?.loginSignUpSuccessfullyText}
        duration={3000}
        isMobile={false}
        isDrawer={false}
        placement="top"
        onToastChange={onLoginSuccessToastChange}
      />
    ) : null;

  /**
   * Syncs header scroll-derived UI (search icon, shadow, scrolled flag, overlay opacity).
   * Safe to call on mount (useLayoutEffect) and on every scroll event.
   * @returns {void}
   */
  const syncHeaderScrollState = useCallback(() => {
    if (typeof document === "undefined") return;

    const scrollY = getHeaderScrollOffset();
    const bannerHeight = getAppBannerHeight();

    if (isMobile) {
      const threshold = THRESHOLD_HEADER_FOR_SEARCH + bannerHeight;
      const hasScrolledPastThreshold = scrollY >= threshold;

      setIsSearchIconVisible(hasScrolledPastThreshold);
      setIsAddShadow(hasScrolledPastThreshold);
      setIsScrolled(hasScrolledPastThreshold);

      if (isTransparentOnMobile) {
        setHeaderBgOpacity(getTransparentHeaderBgOpacity(scrollY, bannerHeight));
      }
    } else {
      setIsAddShadow(scrollY > 0);
    }
  }, [isMobile, isTransparentOnMobile]);

  /**
   * On scroll handler to get the offset of documentElement.
   */
  const onHandleScroll = () => {
    syncHeaderScrollState();
  };

  useEffect(() => {
    // Menu is heavy (deep nested category tree) and identical across pages — fetched
    // client-side here instead of on every SSR request. Only fetched when no
    // server-provided menuData is passed in (kept as an override for any caller
    // still supplying it) and only relevant on desktop, where the mega menu renders.
    if (isMobile || menuData?.length) {
      return undefined;
    }
    let isCancelled = false;
    /**
     * Fetches the mega menu tree (`menuJson`) and stores it, unless the effect was
     * cleaned up first.
     */
    const fetchMenu = async () => {
      try {
        const menuResponse = await getMegaMenuContentAPI(isMobile, catalogId);
        const rawMenu = menuResponse?.menuJson;
        const parsedMenu = typeof rawMenu === "string" ? JSON.parse(rawMenu) : rawMenu || [];
        if (!isCancelled) {
          setClientMenuData(parsedMenu);
        }
      } catch (error) {
        console.log("Error fetching menu data", error);
      }
    };
    fetchMenu();
    return () => {
      isCancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobile, catalogId]);

  useEffect(() => {
    if (checkLoginStatus) {
      dispatch(
        requestToFetchFnpCashBalance({
          isAgent: loginSignUp?.userDetails?.isAgentLogin,
          partyId: loginSignUp?.userDetails?.customerID,
        }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkLoginStatus]);

  useEffect(() => {
    if (loginDetail?.loggedin) {
      dispatch(requestToGetCustomerDetails());
    }
    const timer = setTimeout(() => {
      recallABTestingHook?.();
    }, 1000);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loginDetail?.loggedin]);

  useEffect(() => {
    if (!loginDetail?.loggedin) {
      return;
    }
    const accountRedirectPath = consumeHeaderAccountLoginRedirect();
    if (!accountRedirectPath || window.location.pathname === accountRedirectPath.split("?")[0]) {
      return;
    }
    window.location.href = accountRedirectPath;
  }, [loginDetail?.loggedin]);

  /**
   * This function fetches lottie icons from API and sets in redux store.
   * @async
   * @function fetchDynamicPlaceholder
   */
  const fetchLottieIcons = async () => {
    const lottieIcons = await execDataLayer(getLottieIcons, LOTTIE_ICONS);
    dispatch(requestToSetIcons(lottieIcons));
  };

  useEffect(() => {
    const isUserLoggedIn = loginDetail?.loggedin && !isEmpty(loginSignUp?.userDetails);
    if (isUserLoggedIn && (availableBalance || availableBalance === 0)) {
      const userDetails = { ...loginSignUp?.userDetails, userType: loginDetail?.userType };
      const isOAuth = loginDetail?.isOAuthUser;
      const storageItem = !!getSessionStorage(SOCIAL_LOGIN_KEY);
      const eventName = loginDetail?.userType === "Existing" ? GA4_EVENTS.LOGIN : GA4_EVENTS.SIGNUP;
      if (storageItem) {
        const publishCleverTapData = { profile: { identity: userDetails?.emailID, fnpCash: availableBalance } };
        onClevertapPublish(publishCleverTapData);
        const properties = setGA4LoginAndSignUp(userDetails, isOAuth);
        trackEvent(eventName, { ...properties }, ["ga4"]);
        onUserLogin({
          Site: {
            Identity: userDetails?.emailID,
            Email: userDetails?.emailID,
          },
        });
        if (loginDetail?.isNewUser) {
          const registrationData = {
            is_name_available: !!loginDetail?.fullname,
            is_phone_available: !isOAuth,
            is_email_available: true,
            login_source: isOAuth ? "Gmail" : "Email",
          };
          trackEvent("Registration Success", registrationData, ["clevertap"]);
        }
        trackEvent(
          cleverTapCommonEvents.loginSuccess,
          { loginSuccess: "Login Success", login_method: isOAuth ? "Gmail" : "Email" },
          ["clevertap"],
        );
        removeSessionStorage(SOCIAL_LOGIN_KEY);
      }
    }
  }, [loginSignUp?.userDetails, loginDetail?.loggedin, availableBalance]);

  useEffect(() => {
    dispatch(requestToGetWebFeatureControls());
    dispatch(requestToGetCurrency(currencyListWithHeaders));
    dispatch(initiateFus());
    dispatch(requestToFetchFnpLabel());
    dispatch(requestToGetCurrencyFlagIcon());
    dispatch(requestToFetchSpecialCountries());
    fetchLottieIcons();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** */
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fetchDefaultSuggestions = useCallback(
    async (activeCatalogId) => {
      if (isMobile) {
        return;
      }
      try {
        setIsDefaultLoading(true);
        const widgets = await fetchDefaultSearchPageFeed({
          isMobile: false,
          catalogId: activeCatalogId,
        });
        setDefaultSearchWidgets(widgets);
      } catch (error) {
        console.error("Error fetching default search feed:", error);
        setDefaultSearchWidgets([]);
      } finally {
        setIsDefaultLoading(false);
      }
    },
    [isMobile],
  );

  useEffect(() => {
    // no loginDetail gate: the new chat supports guests, so the handler must exist for them too
    if (typeof window !== "undefined" && window.location.pathname.includes("/info/contact-us")) {
      window.caAssistantChatBot = () => {
        if (window.showFNPChatForWeb) {
          openSiteChat(); // new chat supports guests; no login gate
        } else if (checkLoginStatus) {
          if (!window.dataLayer) {
            window.dataLayer = [];
          }

          window.dataLayer.push({
            event: "Cygnus_event",
            cygnus_id: window?.userDetails?.sid,
          });
        } else {
          setLocalStorageItem("shouldOpenChat", true);
          const from = "/info/contact-us";
          if (isMobile) {
            window.location.href = `/login?from=${from}`;
          } else {
            dispatch(requestToOpenLoginDrawer(true));
          }
        }
      };
    }
  }, [Object.keys(loginDetail).length]);

  // CMS contact-us markup is `<h2 id="openWidget">`; Freshchat's script used to bind it, so bind it ourselves when the new chat is on
  useEffect(() => {
    if (!window.location.pathname.includes("/info/contact-us")) return undefined;
    /**
     * @param {MouseEvent} e click event
     */
    const onClick = (e) => {
      if (window.showFNPChatForWeb && e.target.closest?.("#openWidget")) openSiteChat();
    };
    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, []);

  useEffect(() => {
    if (isSecretSanta) {
      /** */
      const handleHashChange = () => {
        const newHash = window.location.href;
        setHash(newHash);
      };

      handleHashChange();

      window.addEventListener("hashchange", handleHashChange);

      return () => {
        window.removeEventListener("hashchange", handleHashChange);
      };
    }
  }, []);

  useEffect(() => {
    const shouldOpenChat = getLocalStorageItem("shouldOpenChat");
    if (window?.userDetails?.sid && window.location.pathname.includes("/info/contact-us") && shouldOpenChat) {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: "Cygnus_event",
        cygnus_id: window?.userDetails?.sid,
      });
      removeLocalStorageItem("shouldOpenChat");
    }
  }, [Object.keys(loginDetail).length]);

  useEffect(() => {
    document.addEventListener("scroll", onHandleScroll, { passive: true });
    if (isSearchEnabled && !isMobile) {
      fetchDefaultSuggestions(catalogId);
    }
    return () => {
      document.removeEventListener("scroll", onHandleScroll);
    };
  }, [catalogId, fetchDefaultSuggestions, isMobile, isSearchEnabled]);

  // Sync overlay opacity / scrolled state before paint to avoid transparent flash on refresh mid-page
  useLayoutEffect(() => {
    if (!isHomepageMobileOverlay) return undefined;
    syncHeaderScrollState();
    return undefined;
  }, [isHomepageMobileOverlay, syncHeaderScrollState]);

  useEffect(() => {
    if (cartCount > 0) {
      dispatch(responseGetCartItems("Y"));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartCount]);

  if (isLoginPage) {
    return (
      <>
        {loginSuccessToast}
        <header
          className={formatClassNames("mx-auto my-0 flex px-40 py-12", isSecretSanta && "items-center justify-between")}
        >
          <div className="flex justify-start">
            <FNPIcon imgWidth={126} imgHeight={40} parentClasses="w-[126px]" />
          </div>
          {isSecretSanta && hash.includes("#") && (
            <div className="flex gap-16">
              {checkLoginStatus && !hash?.includes("#dashboard") && !urlParams?.get("joinid") && (
                <div>
                  <ButtonComponent
                    size="textBtn"
                    label={MY_GROUPS}
                    classes="py-10 px-12"
                    buttonVariant="outlined"
                    backGroundColor="bg-white-900"
                    // eslint-disable-next-line react/jsx-handler-names
                    onClick={() => {
                      window.location.href = "/santa#dashboard";
                    }}
                  />
                </div>
              )}
              {!hash?.includes("#newRoom") && (
                <div>
                  <ButtonComponent
                    size="textBtn"
                    label={NEW_GROUP}
                    classes="py-10 px-12"
                    // eslint-disable-next-line react/jsx-handler-names
                    onClick={() => {
                      window.location.href = "/santa#newRoom";
                    }}
                  />
                </div>
              )}
            </div>
          )}
        </header>
      </>
    );
  }

  const effectiveHeaderTextIconColor =
    isMobile && isTransparentOnMobile && isScrolled ? undefined : headerTextIconColor;

  return (
    <>
      {loginSuccessToast}
      {isSearchFocused && <div className="absolute top-20 z-50 h-full w-full bg-black-900 bg-opacity-45" />}
      <div
        id="header-child"
        className={getHeaderShellClassNames({
          isMobile,
          isTransparentOnMobile,
          isScrolled,
          isAddShadow,
          isTopBanner,
          catalogId,
        })}
        style={isHomepageMobileOverlay ? { backgroundColor: `rgba(255, 255, 255, ${headerBgOpacity})` } : undefined}
        ref={headerRef}
      >
        <header className="flex items-center justify-between md:px-40 md:py-12">
          <div className="flex w-3/5 justify-start">
            <div className="flex items-center justify-center md:gap-20">
              <span className="hidden md:flex">
                <FNPIcon />
              </span>
              <span className="border-white-smoke hidden h-48 border-r border-solid lg:flex" />
              {isFromPDP || !isShowLocationLock ? (
                <span className="hidden md:block md:w-full" aria-hidden="true" />
              ) : (
                <LocationLockHeader isMobile={isMobile} textIconColor={effectiveHeaderTextIconColor} />
              )}
            </div>

            <span className="relative hidden w-full justify-center  md:mx-40 md:flex md:flex-col">
              {isSearchEnabled && (
                <DesktopSearch
                  catalogId={catalogId}
                  searchQuery={searchQuery}
                  setIsSearchFocused={setIsSearchFocused}
                  defaultSearchWidgets={defaultSearchWidgets}
                  // eslint-disable-next-line react/jsx-handler-names
                  onRequestDefaultSearchFeed={() => {
                    if (!isDefaultLoading && defaultSearchWidgets.length === 0) {
                      fetchDefaultSuggestions(catalogId);
                    }
                  }}
                  isDefaultLoading={isDefaultLoading}
                  isFromPDP={isFromPDP}
                  isAutoSuggestion={autoSuggestion}
                  isSearchFocused={isSearchFocused}
                  isGiftFinderEnabled={isGiftFinderEnabled}
                />
              )}
            </span>
          </div>
          <HeaderToolBar
            isMobile={isMobile}
            serverSelectedCurrency={serverSelectedCurrency}
            isSearchIconVisible={isSearchEnabled && isSearchIconVisible && isShowSearchIcon}
            catalogId={catalogId}
            headerTextIconColor={effectiveHeaderTextIconColor}
          />
          {isShowCartEmpty && <ClearCart isMobile={isMobile} selectedCurrency={selectedCurrency} />}
        </header>

        {!isMobile && !isSearchFocused && (
          <>
            <DesktopMegaMenu
              data={menuData?.length ? menuData : clientMenuData}
              classes="md:flex"
              isTopBanner={isTopBanner}
            />
            {loginSignUp?.openLoginDrawer ? <LoginSignupDrawer /> : null}
          </>
        )}
      </div>
    </>
  );
};
export default React.memo(Header);

// Define PropTypes for the Header component
Header.propTypes = {
  isFromPDP: PropTypes.bool,
  // eslint-disable-next-line max-lines
  isMobile: PropTypes.bool,
  catalogId: PropTypes.string,
  // preloadLinks: PropTypes.arrayOf(
  //   PropTypes.shape({
  //     rel: PropTypes.string.isRequired,
  //     href: PropTypes.string.isRequired,
  //   }),
  // ),
  isLoginPage: PropTypes.bool,
  serverSelectedCurrency: PropTypes.object,
  currencyListWithHeaders: PropTypes.object,
  searchQuery: PropTypes.string,
  isShowSearchIcon: PropTypes.bool,
  menuData: PropTypes.array,
  isTopBanner: PropTypes.bool,
  isSecretSanta: PropTypes.bool,
  isTransparentOnMobile: PropTypes.bool,
  headerTextIconColor: PropTypes.object,
  isGiftFinderEnabled: PropTypes.bool,
  isSearchEnabled: PropTypes.bool,
  isShowLocationLock: PropTypes.bool,
};
